import { ProductVariantLiteModel } from 'api-hooks/product/list';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetProductVariantColumn from 'hooks/use-get-product-variant-column';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TourListFormType, TourProductFormType } from '../form-type';
import { onAddNewProduct } from '../hooks/use-get-default-values';

const name = `tourProducts` as const;

function useGetProductColumnList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const productVariantColumn = useGetProductVariantColumn(true);

  const columns = React.useMemo<IColumn<TourProductFormType>[]>(() => {
    return [
      {
        header: t('product:product-variant'),
        accessorKey: 'productVariantId',
        minSize: 250,
        dataType: ColumnDataType.select,
        required: true,
        selectProps: productVariantColumn,
      },
      ...(editable
        ? [
            {
              header: t('common:stock'),
              accessorKey: 'stock',
              minSize: 100,
              dataType: ColumnDataType.number,
              minNumber: 0,
              disabled: true,
            },
          ]
        : []),
      {
        header: t('common:avg_cogs'),
        accessorKey: 'avgCogs',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        disabled: true,
      },
      {
        header: t('common:quantity'),
        accessorKey: 'qty',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 100,
        dataType: ColumnDataType.text,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, productVariantColumn, t]);

  return columns;
}

export default function ProductTable() {
  const { editable } = useFormState();
  const { clearErrors, control, getValues, trigger } =
    useFormContext<TourListFormType>();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetProductColumnList();

  const dispatch = React.useCallback(
    (action) => {
      const products = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const product = products[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof TourProductFormType;
      const value = action.value;

      const otherVal: () => Partial<TourProductFormType> = () => {
        if (columnId === 'productVariantId') {
          const extra = action?.extra?.[0]?.extra as ProductVariantLiteModel;
          const price = parseFloat(extra?.sellPrice?.toString()) || 0;
          const stock = parseFloat(extra?.stock?.toString()) || 0;

          return {
            price,
            stock,
            avgCogs: price * (product.qty || 0),
          };
        }
        if (columnId === 'qty') {
          return {
            avgCogs: (product.price || 0) * (action.value || 0),
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(onAddNewProduct());
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...product,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}`);
      }
    },
    [append, clearErrors, getValues, remove, trigger, update],
  );

  return (
    <div style={{ width: '100' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
